/**
 * OpprettFristModal - Modal for å opprette ny frist fra Dashboard
 * Skiller mellom lovpålagte frister og ordinære gjøremål
 */

import React, { useState, useEffect } from 'react';
import crmApi from '../api/crm';
import './OpprettFristModal.css';

export default function OpprettFristModal({ onClose, onSuccess }) {
  const [fristType, setFristType] = useState('lovpålagt'); // 'lovpålagt' eller 'gjøremål'
  const [saker, setSaker] = useState([]);
  const [formData, setFormData] = useState({
    sak_id: '',
    tittel: '',
    beskrivelse: '',
    frist_dato: '',
    påminnelse_dager: 7,
    lovpålagt_type: 'relativ_reklamasjon', // For lovpålagte frister
    prioritet: 'høy',
    kritiskhet: 'normal'
  });

  const lovpålagteTyper = [
    { value: 'relativ_reklamasjon', label: 'Relativ reklamasjon', standard_dager: 14 },
    { value: 'absolutt_reklamasjon', label: 'Absolutt reklamasjon', standard_dager: 730 }, // 2 år
    { value: 'foreldelse', label: 'Foreldelse', standard_dager: 1095 }, // 3 år
    { value: 'passivitet', label: 'Passivitet', standard_dager: 90 },
    { value: 'tilsvarsfrist', label: 'Tilsvarsfrist', standard_dager: 21 },
    { value: 'annet_rett', label: 'Annet fra retten', standard_dager: 14 }
  ];

  useEffect(() => {
    lastSaker();
  }, []);

  const lastSaker = async () => {
    try {
      const data = await crmApi.getSaker({ status: 'aktiv' });
      const sakerArray = Array.isArray(data) ? data : (data.saker || []);
      setSaker(sakerArray);
    } catch (error) {
      console.error('Feil ved lasting av saker:', error);
    }
  };

  const handleTypeChange = (type) => {
    setFristType(type);

    // Sett standard prioritet og kritiskhet basert på type
    if (type === 'lovpålagt') {
      setFormData({ ...formData, prioritet: 'urgent', kritiskhet: 'absolutt' });
    } else {
      setFormData({ ...formData, prioritet: 'middels', kritiskhet: 'normal' });
    }
  };

  const handleLovpålagtTypeChange = (type) => {
    const valgtType = lovpålagteTyper.find(t => t.value === type);

    // Beregn standard frist-dato basert på type
    const iDag = new Date();
    const fristDato = new Date(iDag.getTime() + valgtType.standard_dager * 24 * 60 * 60 * 1000);
    const fristDatoString = fristDato.toISOString().split('T')[0];

    setFormData({
      ...formData,
      lovpålagt_type: type,
      tittel: valgtType.label,
      frist_dato: fristDatoString,
      prioritet: 'urgent',
      kritiskhet: 'absolutt'
    });
  };

  const handleOpprett = async () => {
    if (!formData.sak_id) {
      alert('Vennligst velg en sak');
      return;
    }

    if (!formData.tittel.trim()) {
      alert('Vennligst fyll inn tittel');
      return;
    }

    if (!formData.frist_dato) {
      alert('Vennligst velg en frist-dato');
      return;
    }

    try {
      // Formater data for backend
      const fristData = {
        sak_id: formData.sak_id,
        beskrivelse: formData.tittel,
        frist_dato: new Date(formData.frist_dato).getTime(),
        prioritet: formData.prioritet,
        kritiskhet: formData.kritiskhet,
        status: 'aktiv',
        // Ekstra metadata i notater-feltet
        notater: JSON.stringify({
          type: fristType, // 'lovpålagt' eller 'gjøremål'
          lovpålagt_type: fristType === 'lovpålagt' ? formData.lovpålagt_type : null,
          reminder_days: parseInt(formData.påminnelse_dager),
          extra_beskrivelse: formData.beskrivelse || null
        })
      };

      await crmApi.request('/api/frister', {
        method: 'POST',
        body: JSON.stringify(fristData)
      });

      alert(`✅ ${fristType === 'lovpålagt' ? 'Lovpålagt frist' : 'Gjøremål'} opprettet!`);

      if (onSuccess) {
        onSuccess();
      }
      onClose();

    } catch (error) {
      console.error('Feil ved oppretting av frist:', error);
      alert('Kunne ikke opprette frist: ' + error.message);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content opprett-frist-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>📅 Opprett ny frist</h2>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>

        {/* Type-velger */}
        <div className="frist-type-velger">
          <button
            className={`type-btn ${fristType === 'lovpålagt' ? 'active' : ''}`}
            onClick={() => handleTypeChange('lovpålagt')}
          >
            ⚖️ Lovpålagt frist
          </button>
          <button
            className={`type-btn ${fristType === 'gjøremål' ? 'active' : ''}`}
            onClick={() => handleTypeChange('gjøremål')}
          >
            ✅ Gjøremål
          </button>
        </div>

        {/* Lovpålagt frist-type (kun hvis lovpålagt) */}
        {fristType === 'lovpålagt' && (
          <div className="form-group lovpålagt-type-gruppe">
            <label>Type lovpålagt frist *</label>
            <select
              value={formData.lovpålagt_type}
              onChange={(e) => handleLovpålagtTypeChange(e.target.value)}
              className="lovpålagt-select"
            >
              {lovpålagteTyper.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label} (standard {type.standard_dager} dager)
                </option>
              ))}
            </select>
            <small className="form-hint">
              ⚠️ Lovpålagte frister er kritiske og må overholdes
            </small>
          </div>
        )}

        {/* Velg sak */}
        <div className="form-group">
          <label>Sak *</label>
          <select
            value={formData.sak_id}
            onChange={(e) => setFormData({ ...formData, sak_id: e.target.value })}
            required
          >
            <option value="">-- Velg sak --</option>
            {saker.map(sak => (
              <option key={sak.id} value={sak.id}>
                {sak.saksnummer} - {sak.tittel || sak.klient_navn}
              </option>
            ))}
          </select>
        </div>

        {/* Tittel (pre-fylles for lovpålagt) */}
        <div className="form-group">
          <label>Tittel *</label>
          <input
            type="text"
            value={formData.tittel}
            onChange={(e) => setFormData({ ...formData, tittel: e.target.value })}
            placeholder={fristType === 'lovpålagt' ? 'Velges automatisk basert på type' : 'F.eks. "Gjennomgå bevismateriale"'}
            required
          />
        </div>

        {/* Beskrivelse */}
        <div className="form-group">
          <label>Beskrivelse (valgfri)</label>
          <textarea
            value={formData.beskrivelse}
            onChange={(e) => setFormData({ ...formData, beskrivelse: e.target.value })}
            placeholder="Ekstra detaljer om fristen..."
            rows={3}
          />
        </div>

        {/* Frist-dato */}
        <div className="form-row">
          <div className="form-group">
            <label>Frist-dato *</label>
            <input
              type="date"
              value={formData.frist_dato}
              onChange={(e) => setFormData({ ...formData, frist_dato: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>Påminnelse (dager før)</label>
            <input
              type="number"
              value={formData.påminnelse_dager}
              onChange={(e) => setFormData({ ...formData, påminnelse_dager: e.target.value })}
              min="1"
              max="90"
            />
          </div>
        </div>

        {/* Prioritet */}
        <div className="form-group">
          <label>Prioritet</label>
          <select
            value={formData.prioritet}
            onChange={(e) => setFormData({ ...formData, prioritet: e.target.value })}
          >
            <option value="urgent">🔥 Urgent</option>
            <option value="høy">🔴 Høy</option>
            <option value="middels">🟡 Middels</option>
            <option value="lav">🟢 Lav</option>
          </select>
        </div>

        {/* Kritiskhetsnivå */}
        <div className="form-group">
          <label>Kritiskhetsnivå</label>
          <select
            value={formData.kritiskhet}
            onChange={(e) => setFormData({ ...formData, kritiskhet: e.target.value })}
          >
            <option value="normal">⚪ Normal</option>
            <option value="viktig">🟡 Viktig (varsles hver time)</option>
            <option value="absolutt">🔴 Absolutt (kritisk varsling)</option>
          </select>
          <small className="form-hint">
            ⚠️ Viktig/absolutt gir timevarsler ved forfall
          </small>
        </div>

        {/* Varsling for lovpålagte frister */}
        {fristType === 'lovpålagt' && (
          <div className="varsling-boks">
            <p>⚖️ <strong>Lovpålagt frist</strong></p>
            <p style={{fontSize: '0.9rem', marginTop: '5px'}}>
              Dette er en kritisk frist som må overholdes. Du vil få påminnelse {formData.påminnelse_dager} dager før fristen går ut.
            </p>
          </div>
        )}

        <div className="modal-actions">
          <button className="btn-secondary" onClick={onClose}>
            Avbryt
          </button>
          <button className="btn-primary" onClick={handleOpprett}>
            Opprett {fristType === 'lovpålagt' ? 'frist' : 'gjøremål'}
          </button>
        </div>
      </div>
    </div>
  );
}
